<template>
    <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
        <div class="text-[#414141] font-sans">
            <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">Core Annual Program Expenses</h3>
            <p class="text-[14px] leading-[22px] mb-[0.5rem]">Program expenses are the costs directly associated with the delivery of your organization's Core Annual Programs.</p>
            
            <p class="text-[14px] leading-[22px] mb-[0.5rem]">Example line-items include salaries and benefits for program staff and contractors, supplies and materials for program delivery, program-related travel expenses, rent or utilities for program facilities, equipment and technology used for program activities, fees for program evaluation and monitoring, and the estimated value of in-kind goods or services received.</p>

            <p class="text-[14px] leading-[22px] mb-[1.5rem]">For each of your Core Annual Programs, enter the annual cost for administering the program by line-item category and the estimated costs to run and/or expand these programs over the next three (3) years. If your organization receives in-kind goods or services, these costs should be accounted for in your budget.</p>

            <div v-if="isLoading" class="flex justify-center items-center h-[200px]">
                <ActivityIndicator />
            </div>

            <!-- Program Tabs -->
            <div v-else class="mb-6">
                <div class="flex space-x-4 border-b border-gray-200">
                    <button
                        v-for="(program, index) in corePrograms"
                        :key="program.id || index"
                        @click="handleProgramChange(index)"
                        :class="[
                            'px-4 py-2 text-sm font-medium border-b-2 -mb-px',
                            activeProgramIndex === index
                                ? 'border-[#00423D] text-[#00423D]'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        ]"
                    >
                        {{ program.program_name || `Program ${index + 1}` }}
                    </button>
                </div>
            </div>

            <!-- Budget Table for Active Program -->
            <div v-if="corePrograms.length > 0">
                <div class="mb-4">
                    <h4 class="font-['Open_Sans'] font-semibold text-[18px] leading-[140%] text-[#414141] align-middle">{{ corePrograms[activeProgramIndex].program_name }}</h4>
                </div>
                <BudgetTable 
                    :categories="programCategories[activeProgramIndex]"
                    :total-programs-budget="total_programs_budget"
                    @delete-item="handleDeleteItem"
                />
            </div>
            <div v-else class="text-center py-8 text-gray-500">
                Please add programs in the Core Annual Programs section first.
            </div>
        </div>
    </div>

    <!-- Confirmation Popup -->
    <div v-if="conformationPopupIsOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 class="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p class="text-gray-600 mb-6">Are you sure you want to delete this item?</p>
            <div class="flex justify-end space-x-4">
                <button
                    @click="onCancelRemove"
                    class="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                    Cancel
                </button>
                <button
                    @click="onConformRemove"
                    class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Delete
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStepValidation } from '@/composables/useStepValidation';
import BudgetTable from '@/components/BudgetTable.vue';
import ActivityIndicator from '@/components/ActivityIndicator.vue';
import { useAuthStore } from '@/stores/auth';
import { useRoutesManagement } from '@/stores/routesManagement';
import { useFetchCache } from '@/composables/fetchCache';
import { useFetch } from '@/composables/fetch';
import { usePostAnswer } from '@/composables/postAnswer';
import global from "@/global";

const router = useRouter();
const authStore = useAuthStore();
const routesManagementStore = useRoutesManagement();

// Use the validation composable with default settings
useStepValidation();

const isLoading = ref(true);
const total_programs_budget = ref(0);
const conformationPopupIsOpen = ref(false);
const deletionFunction = ref(null);
const activeProgramIndex = ref(0);
const corePrograms = ref([]);
const programCategories = ref([]);

const EXPENSE_TYPES = [
  'Personnel and Benefits',
  'Finance Fees',
  'Legal Fees',
  'Printing and Publications',
  'Travel, Conferences, and Meetings',
  'Marketing and Communications',
  'Other Expenses',
  'In-Kind Goods & Services'
];

// Add this mapping at the top, after EXPENSE_TYPES
const API_CATEGORY_KEY_MAP = {
  'Personnel and Benefits': 'personnel_and_benefits',
  'Finance Fees': 'finance_fees',
  'Legal Fees': 'legal_fees',
  'Printing and Publications': 'printing_and_publications',
  'Travel, Conferences, and Meetings': 'travel_conferences_and_meetings',
  'Marketing and Communications': 'marketing_and_communications',
  'Other Expenses': 'other_expenses',
  'In-Kind Goods & Services': 'in-kind_goods_services',
  'Programs': 'programs',
};

// API Integration using composables
const { error: fetchError, fetchCachedData, cachedData } = useFetchCache({
  uri: "/questions/annual_programs_expenses",
  headers: {
    Authorization: `Bearer ${authStore.user.accessToken}`,
  },
});

const expensesFetch = useFetch({
  uri: "/expenses",
  headers: {
    Authorization: `Bearer ${authStore.user.accessToken}`,
  },
});

const deleteExpenseFetch = useFetch({
  uri: "/expenses",
  headers: {
    Authorization: `Bearer ${authStore.user.accessToken}`,
  },
  method: 'DELETE',
});

const answerPost = usePostAnswer({
  uri: `/expenses`,
  headers: {
    Authorization: `Bearer ${authStore.user.accessToken}`,
    "Content-Type": "application/json",
  },
});

// Initialize data
onMounted(async () => {
  try {
    // Fetch question data
    await fetchCachedData();
    localStorage.setItem(
      "questionFetchData",
      JSON.stringify(cachedData.value.question)
    );

    // Fetch core programs
    const coreProgramsResponse = await fetch(`${global.API_URL}/core-programs`, {
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });
    const coreProgramsData = await coreProgramsResponse.json();
    corePrograms.value = coreProgramsData.corePrograms || [];

    // Initialize categories for each program
    programCategories.value = corePrograms.value.map(() => 
      EXPENSE_TYPES.map(type => ({
        name: type,
        isOpen: type === 'Personnel and Benefits',
        items: []
      }))
    );

    // Fetch expenses for each program
    await Promise.all(corePrograms.value.map(async (program, index) => {
      const response = await fetch(`${global.API_URL}/expenses?program_id=${program.id}`, {
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
        },
      });
      const data = await response.json();
      
      // Map expenses to categories for this program
      programCategories.value[index] = EXPENSE_TYPES.map(type => {
        const categoryKey = API_CATEGORY_KEY_MAP[type];
        const categoryExpenses = data.expenses[categoryKey] || [];
        return {
          name: type,
          isOpen: type === 'Personnel and Benefits',
          items: categoryExpenses.map(expense => ({
            id: expense.id,
            name: expense.expense_description,
            year1: Number(expense.expense_cost),
            year2: Number(expense.expense_cost_year_2),
            year3: Number(expense.expense_cost_year_3)
          }))
        };
      });
    }));

    // Calculate total programs budget
    if (expensesFetch.fetchedData.value.programs && expensesFetch.fetchedData.value.programs.length > 0) {
      total_programs_budget.value = Number(expensesFetch.fetchedData.value.programs[0].expense_cost);
    }

    isLoading.value = false;
  } catch (error) {
    console.error('Error loading data:', error);
    isLoading.value = false;
  }
});

// Handle program change
const handleProgramChange = (index) => {
  activeProgramIndex.value = index;
};

// Delete confirmation popup handlers
function onCancelRemove() {
  conformationPopupIsOpen.value = false;
}

async function onConformRemove() {
  conformationPopupIsOpen.value = false;
  await deletionFunction.value();
  deletionFunction.value = null;
}

// Delete expense
const deleteExpense = async (expenseId) => {
  try {
    const response = await fetch(`${global.API_URL}/expenses/${expenseId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });
    const data = await response.json();
    return data?.status || false;
  } catch (error) {
    console.error('Error deleting expense:', error);
    return false;
  }
};

// Save expenses data
const saveExpenses = async () => {
  try {
    // Process expenses for all programs
    const allExpensesToSave = programCategories.value.flatMap((programCategories, programIndex) => 
      programCategories.flatMap(category => 
        category.items.map(item => ({
          id: item.id,
          expense_type: category.name,
          expense_description: item.name,
          expense_cost: item.year1.toString().replace(/,/g, ""),
          expense_cost_year_2: item.year2.toString().replace(/,/g, ""),
          expense_cost_year_3: item.year3.toString().replace(/,/g, ""),
          expenseable_type: "CoreProgram",
          expenseable_id: corePrograms.value[programIndex].id
        }))
      )
    );
    
    // Make API call to save all expenses
    const response = await fetch(`${global.API_URL}/expenses`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
      body: JSON.stringify({
        expenses: allExpensesToSave,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to save expenses');
    }

    const data = await response.json();
    
    // Refresh expenses data after successful save
    await Promise.all(corePrograms.value.map(async (program, index) => {
      const response = await fetch(`${global.API_URL}/expenses?program_id=${program.id}`, {
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
        },
      });
      const data = await response.json();
      
      // Update categories for this program
      programCategories.value[index] = EXPENSE_TYPES.map(type => {
        const categoryKey = API_CATEGORY_KEY_MAP[type];
        const categoryExpenses = data.expenses[categoryKey] || [];
        return {
          name: type,
          isOpen: type === 'Personnel and Benefits',
          items: categoryExpenses.map(expense => ({
            id: expense.id,
            name: expense.expense_description,
            year1: Number(expense.expense_cost),
            year2: Number(expense.expense_cost_year_2),
            year3: Number(expense.expense_cost_year_3)
          }))
        };
      });
    }));

    return data;
  } catch (error) {
    console.error("Error saving expenses:", error);
    throw error;
  }
};

// Event handlers
const handleDeleteItem = async (categoryIndex, itemIndex) => {
  const item = programCategories.value[activeProgramIndex.value][categoryIndex].items[itemIndex];
  
  if (item.id) {
    conformationPopupIsOpen.value = true;
    deletionFunction.value = async () => {
      const success = await deleteExpense(item.id);
      if (success) {
        programCategories.value[activeProgramIndex.value][categoryIndex].items.splice(itemIndex, 1);
      } else {
        console.error('Failed to delete expense');
      }
    };
  } else {
    programCategories.value[activeProgramIndex.value][categoryIndex].items.splice(itemIndex, 1);
  }
};

// Expose saveExpenses to parent component
defineExpose({
  saveExpenses,
  handleDeleteItem
});
</script>

<style scoped>
</style> 